import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";

import SplashScreen from "@/pages/SplashScreen";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";

import CustomerLayout from "@/components/layout/CustomerLayout";
import HomePage from "@/pages/customer/HomePage";
import RestaurantPage from "@/pages/customer/RestaurantPage";
import CartPage from "@/pages/customer/CartPage";
import CheckoutPage from "@/pages/customer/CheckoutPage";
import TrackingPage from "@/pages/customer/TrackingPage";
import OrdersPage from "@/pages/customer/OrdersPage";
import ProfilePage from "@/pages/customer/ProfilePage";
import FavoritesPage from "@/pages/customer/FavoritesPage";
import PaymentSuccess from "@/pages/customer/PaymentSuccess";
import PaymentCancel from "@/pages/customer/PaymentCancel";

import DriverDashboard from "@/pages/driver/DriverDashboard";
import DriverOnboardingPage from "@/pages/driver/DriverOnboardingPage";
import RestaurantDashboard from "@/pages/restaurant/RestaurantDashboard";
import AdminDashboard from "@/pages/admin/AdminDashboard";

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();

  useEffect(() => {
    let hasProcessed = false;
    const run = async () => {
      if (hasProcessed) return;
      hasProcessed = true;
      const hash = location.hash || window.location.hash;
      const match = hash.match(/session_id=([^&]+)/);
      if (!match) { navigate("/login"); return; }
      try {
        await api.post("/auth/google/session", { session_id: match[1] });
        await refresh();
        navigate("/home", { replace: true });
      } catch (e) {
        console.error(e);
        navigate("/login?err=oauth");
      }
    };
    run();
  }, [location, navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-eatup-charcoal font-display text-xl">Signing you in…</div>
    </div>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user, checking } = useAuth();
  if (checking || user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-eatup-gray">
        <div className="animate-pulse text-eatup-charcoal font-display text-xl">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && roles.length && !roles.includes(user.role) && user.role !== "admin") {
    return <Navigate to="/home" replace />;
  }
  return children;
}

function RootRouter() {
  const location = useLocation();
  // Handle OAuth callback with hash fragment across any route
  if (location.hash?.includes("session_id=") && location.pathname !== "/auth/callback") {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<SplashScreen />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Customer — browsing is public. Only order-history / profile need a login. */}
      <Route element={<CustomerLayout />}>
        <Route path="/home" element={<HomePage />} />
        <Route path="/restaurant/:id" element={<RestaurantPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/track/:orderId" element={<TrackingPage />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
      </Route>
      <Route element={<ProtectedRoute><CustomerLayout /></ProtectedRoute>}>
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/favorites" element={<FavoritesPage />} />
      </Route>

      {/* Driver */}
      <Route path="/driver" element={<ProtectedRoute roles={["driver"]}><DriverDashboard /></ProtectedRoute>} />
      <Route path="/driver/onboarding" element={<ProtectedRoute roles={["driver"]}><DriverOnboardingPage /></ProtectedRoute>} />

      {/* Restaurant */}
      <Route path="/restaurant-admin" element={<ProtectedRoute roles={["restaurant"]}><RestaurantDashboard /></ProtectedRoute>} />

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <RootRouter />
            <Toaster position="top-center" richColors />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
